import { formatDate, NgTemplateOutlet } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
  ViewContainerRef,
  type TemplateRef,
} from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CdkDrag, CdkDropList, moveItemInArray, type CdkDragDrop } from '@angular/cdk/drag-drop';
import { firstValueFrom, type Subscription } from 'rxjs';
import { ScTextInputComponent } from '../fields/text-input.component';
import { applyGridQuery } from '../grid/apply-grid-query';
import { compareKeys, getValue, groupKey } from '../grid/compare';
import type { GridFieldType, GridQuery, GridSort, GridSpec } from '../grid/grid-query';
import type { GridSource } from '../grid/grid-source';
import { normalizeNeedle, normalizeText } from '../grid/grid-text';
import { UI_TEXT } from '../i18n';
import { snapToPixels } from '../overlay-snap';
import {
  ScColumnDirective,
  ScDetailDirective,
  type ScColumnType,
  type ScGroupInfo,
} from './column.directive';

/** Espera de la búsqueda antes de aplicarse (DevExtreme la aplica al dejar de escribir). */
const SEARCH_DELAY_MS = 300;
/** Filas por página en modo remoto si la pantalla no pide otra cosa (la API pide de 1 a 500). */
const REMOTE_PAGE_SIZE = 20;
/** Ancho mínimo de una columna al redimensionarla, si no declara `minWidth`. */
const MIN_COLUMN_WIDTH = 40;
/** Nombre del resaltado de la búsqueda (CSS Custom Highlight API; ver kit.css). */
const HIGHLIGHT = 'sck-table-search';

interface Column {
  /** Posición entre las `<sc-column>` declaradas (no cambia al reordenar). */
  index: number;
  def: ScColumnDirective;
}

/** Correlativo para los ids de las listas del panel de grupos. */
let nextTableId = 0;

/** Un ítem del menú contextual. */
interface MenuEntry {
  label: string;
  icon?: string;
  disabled?: boolean;
  /** Un separador antes de este ítem. */
  separator?: boolean;
  action: () => void;
}

/** Una opción del filtro de encabezado: lo que se ve y los valores que filtra. */
interface FilterOption {
  key: string;
  text: string;
  values: unknown[];
  selected: boolean;
}

/** Lo que se pinta en el cuerpo: una fila de grupo o una fila de datos. */
type ViewItem =
  | { kind: 'group'; id: string; level: number; open: boolean; group: ScGroupInfo; column: Column }
  | { kind: 'row'; id: unknown; row: ViewRow; alt: boolean };

/** Una fila a pintar: la original, su clave y, por columna (índice declarado), valor y texto. */
interface ViewRow {
  key: unknown;
  row: unknown;
  values: unknown[];
  texts: string[];
}

/**
 * Fila envuelta del modo local: `v<i>` y `t<i>` son el valor y el texto de la columna `i`; `n` es su
 * posición (el desempate: sin orden elegido quedan como llegaron, como en DevExtreme) e `id`, su clave.
 */
type LocalRow = Record<string, unknown> & { row: unknown; n: number; id: unknown };

const DATA_TYPE: Record<ScColumnType, GridFieldType> = {
  string: 'string',
  number: 'number',
  date: 'date',
  datetime: 'date',
  boolean: 'boolean',
};

/** El texto de una celda: `displayText`, la fecha con su formato o el valor. */
function textOf(def: ScColumnDirective, value: unknown, row: unknown): string {
  const custom = def.displayText();
  if (custom) return custom(value, row);
  if (value === null || value === undefined) return '';
  const type = def.dataType();
  if (type === 'date' || type === 'datetime') {
    const date = value instanceof Date ? value : new Date(value as string);
    if (Number.isNaN(date.getTime())) return '';
    return formatDate(
      date,
      def.format() ?? (type === 'date' ? 'dd/MM/yyyy' : 'dd/MM/yyyy HH:mm'),
      'en-US',
    );
  }
  return String(value);
}

/** Resaltados de todas las tablas de la página (un solo `Highlight` con nombre, en kit.css). */
const highlighted = new Map<object, Range[]>();

function paintHighlights(owner: object, ranges: Range[]): void {
  const registry = (globalThis as { CSS?: { highlights?: Map<string, unknown> } }).CSS?.highlights;
  const Highlight = (globalThis as { Highlight?: new (...ranges: Range[]) => unknown }).Highlight;
  if (!registry || !Highlight) return;
  if (ranges.length) highlighted.set(owner, ranges);
  else highlighted.delete(owner);
  const all = [...highlighted.values()].flat();
  if (all.length) registry.set(HIGHLIGHT, new Highlight(...all));
  else registry.delete(HIGHLIGHT);
}

/** Rangos de `text` que contienen `needle` (normalizado), mapeados a las posiciones originales. */
function matchRanges(node: Text, needle: string): Range[] {
  const text = node.data;
  let normalized = '';
  const origin: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const piece = normalizeText(text[i]);
    for (let j = 0; j < piece.length; j++) origin.push(i);
    normalized += piece;
  }
  const ranges: Range[] = [];
  let from = normalized.indexOf(needle);
  while (from >= 0 && needle) {
    const range = document.createRange();
    range.setStart(node, origin[from]);
    range.setEnd(node, origin[from + needle.length - 1] + 1);
    ranges.push(range);
    from = normalized.indexOf(needle, from + needle.length);
  }
  return ranges;
}

/**
 * Grilla de lectura del kit (reemplaza `dx-data-grid`): columnas declarativas (`<sc-column>`), orden
 * por clic (Shift suma columnas), búsqueda con espera y resaltado, paginación, fila de detalle y
 * «Actualizar».
 * - **Local** (`[rows]`): todas las filas en el navegador; ordena y busca con `applyGridQuery` sobre
 *   filas envueltas, así una columna calculada (`value`) se busca por su texto sin tocar las filas.
 * - **Remota** (`[source]`, un `remoteSource`): la API resuelve la consulta sobre el total y devuelve
 *   la página. Mientras carga se ven las filas anteriores; si falla, «Reintentar».
 * El aspecto es el de dx-data-grid con las reglas del BackOffice (kit.css, «Tabla»).
 */
@Component({
  selector: 'sc-table',
  imports: [CdkDrag, CdkDropList, CdkTrapFocus, NgTemplateOutlet, ScTextInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="sck-table"
      role="group"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-busy]="loading() || null"
      [class.sck-table--loading]="loading()"
    >
      @if (searchPanel() || groupPanel() || refreshButton()) {
        <div class="sck-table__toolbar" role="toolbar" [attr.aria-label]="text.toolbar">
          @if (groupPanel()) {
            <div
              class="sck-table__group-panel"
              cdkDropList
              cdkDropListOrientation="horizontal"
              [id]="groupListId"
              [cdkDropListEnterPredicate]="canDropOnPanel"
              (cdkDropListDropped)="onGroupDrop($event)"
            >
              @for (index of groupLevels(); track index) {
                <div
                  class="sck-table__group-chip"
                  role="button"
                  tabindex="0"
                  [attr.aria-roledescription]="
                    groupDir(index) === 'asc' ? text.sortedAscending : text.sortedDescending
                  "
                  (click)="toggleGroupSort(index)"
                  (keydown.enter)="toggleGroupSort(index)"
                  (contextmenu)="openChipMenu(index, $event)"
                >
                  <span class="sck-table__group-chip-text">{{ declared()[index].caption() }}</span>
                  <span
                    class="material-icons sck-icon sck-table__chip-sort"
                    [attr.data-icon]="groupDir(index) === 'asc' ? 'arrow_upward' : 'arrow_downward'"
                    aria-hidden="true"
                  ></span>
                  @let grouped = columnAt(index);
                  @if (filterable(grouped)) {
                    <span
                      class="material-icons sck-icon sck-table__filter sck-table__chip-filter"
                      data-icon="filter_alt"
                      role="button"
                      tabindex="0"
                      aria-haspopup="dialog"
                      [class.sck-table__filter--active]="filters().has(index)"
                      [attr.aria-label]="text.filterButton(grouped.def.caption())"
                      (click)="openFilter(grouped, $event)"
                      (keydown.enter)="openFilter(grouped, $event)"
                      (keydown.space)="openFilter(grouped, $event)"
                    ></span>
                  }
                </div>
              } @empty {
                <div class="sck-table__group-message">{{ text.groupPanel }}</div>
              }
            </div>
          }
          <div class="sck-table__toolbar-after">
            @if (refreshButton()) {
              <button
                type="button"
                class="sck-table__refresh"
                [attr.aria-label]="text.refresh"
                [title]="text.refresh"
                (click)="onRefresh()"
              >
                <span class="material-icons sck-icon" data-icon="refresh" aria-hidden="true"></span>
              </button>
            }
            @if (searchPanel()) {
              <sc-text-input
                class="sck-table__search"
                type="search"
                updateOn="input"
                [ariaLabel]="text.searchGrid"
                [placeholder]="text.search"
                [value]="searchText()"
                (valueChange)="onSearch($event)"
              />
            }
          </div>
        </div>
      }
      <div class="sck-table__body">
        <table class="sck-table__grid" role="grid" [class.sck-table__grid--wrap]="wordWrap()">
          <colgroup>
            @for (level of groupSpaces(); track level) {
              <col class="sck-table__group-col" />
            }
            @if (detail()) {
              <col class="sck-table__expand-col" />
            }
            @for (column of columns(); track column.index) {
              <col [style.width.px]="widths().get(column.index) ?? column.def.minWidth()" />
            }
          </colgroup>
          <thead>
            <tr
              role="row"
              class="sck-table__header-row"
              cdkDropList
              cdkDropListOrientation="horizontal"
              [cdkDropListDisabled]="!allowColumnReordering() && !canGroup()"
              [cdkDropListSortingDisabled]="!allowColumnReordering()"
              [cdkDropListConnectedTo]="canGroup() ? [groupListId] : []"
              (cdkDropListDropped)="onColumnDrop($event)"
            >
              @for (level of groupSpaces(); track level) {
                <td role="gridcell" class="sck-table__group-space"></td>
              }
              @if (detail()) {
                <td role="gridcell" class="sck-table__expand-head"></td>
              }
              @for (column of columns(); track column.index) {
                <th
                  role="columnheader"
                  class="sck-table__header"
                  [class.sck-table__header--sortable]="sortable(column)"
                  [class.sck-table__header--end]="align(column) === 'right'"
                  [style.--sck-table-reserve]="reserve(column) + 'px'"
                  [style.min-width.px]="column.def.minWidth()"
                  [attr.aria-label]="text.column(column.def.caption())"
                  [attr.aria-sort]="ariaSort(column)"
                  [attr.tabindex]="sortable(column) ? 0 : null"
                  [style.text-align]="align(column)"
                  (click)="onHeaderClick(column, $event)"
                  (keydown.enter)="onHeaderClick(column, $event)"
                  cdkDrag
                  [cdkDragData]="column.index"
                  [cdkDragLockAxis]="canGroup() ? null : 'x'"
                  (contextmenu)="openHeaderMenu(column, $event)"
                  [cdkDragDisabled]="!allowColumnReordering() && !groupable(column)"
                  (cdkDragStarted)="headerDragged = true"
                  (cdkDragEnded)="endHeaderDrag()"
                >
                  @if (filterable(column) || sortDir(column)) {
                    <span class="sck-table__indicators">
                      @if (filterable(column)) {
                        <span
                          class="material-icons sck-icon sck-table__filter"
                          data-icon="filter_alt"
                          role="button"
                          tabindex="0"
                          aria-haspopup="dialog"
                          [class.sck-table__filter--active]="filters().has(column.index)"
                          [attr.aria-label]="text.filterButton(column.def.caption())"
                          (click)="openFilter(column, $event)"
                          (keydown.enter)="openFilter(column, $event)"
                          (keydown.space)="openFilter(column, $event)"
                        ></span>
                      }
                      @if (sortDir(column); as dir) {
                        <span
                          class="material-icons sck-icon sck-table__sort"
                          [attr.data-icon]="dir === 'asc' ? 'arrow_upward' : 'arrow_downward'"
                          aria-hidden="true"
                        ></span>
                        @if (sortIndex(column); as position) {
                          <span class="sck-table__sort-index">{{ position }}</span>
                        }
                      }
                    </span>
                  }
                  <span class="sck-table__header-text">{{ column.def.caption() }}</span>
                  @if (allowColumnResizing()) {
                    <span
                      class="sck-table__resizer"
                      aria-hidden="true"
                      (pointerdown)="onResizeStart(column, $event)"
                      (click)="$event.stopPropagation()"
                    ></span>
                  }
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (entry of items(); track entry.id) {
              @if (entry.kind === 'group') {
                <tr
                  role="row"
                  class="sck-table__group-row"
                  [class.sck-table__group-row--nested]="entry.level > 0"
                  [attr.aria-expanded]="entry.open"
                >
                  @for (level of groupSpaces().slice(0, entry.level); track level) {
                    <td class="sck-table__group-space"></td>
                  }
                  <td
                    role="gridcell"
                    class="sck-table__group-expand"
                    (click)="toggleGroup(entry.id)"
                  >
                    <button
                      type="button"
                      class="sck-table__expand-button"
                      [attr.aria-expanded]="entry.open"
                      [attr.aria-label]="entry.open ? text.collapse : text.expand"
                      (click)="$event.stopPropagation(); toggleGroup(entry.id)"
                    >
                      <span
                        class="material-icons sck-icon sck-table__chevron"
                        [attr.data-icon]="entry.open ? 'arrow_drop_down' : 'arrow_right'"
                        aria-hidden="true"
                      ></span>
                    </button>
                  </td>
                  <td
                    role="gridcell"
                    class="sck-table__group-cell"
                    [attr.colspan]="totalColumns() - entry.level - 1"
                  >
                    @if (entry.column.def.group(); as group) {
                      <ng-container
                        [ngTemplateOutlet]="group.template"
                        [ngTemplateOutletContext]="{ $implicit: entry.group }"
                      />
                    } @else {
                      {{ entry.group.text }}
                    }
                  </td>
                </tr>
              } @else {
                @let item = entry.row;
                <tr
                  role="row"
                  class="sck-table__row"
                  [class.sck-table__row--alt]="rowAlternation() && entry.alt"
                >
                  @for (level of groupSpaces(); track level) {
                    <td class="sck-table__group-space"></td>
                  }
                  @if (detail()) {
                    <td role="gridcell" class="sck-table__expand" (click)="toggle(item)">
                      <button
                        type="button"
                        class="sck-table__expand-button"
                        [attr.aria-expanded]="isOpen(item)"
                        [attr.aria-label]="isOpen(item) ? text.collapse : text.expand"
                        (click)="$event.stopPropagation(); toggle(item)"
                      >
                        <span
                          class="material-icons sck-icon sck-table__chevron"
                          [attr.data-icon]="isOpen(item) ? 'arrow_drop_down' : 'arrow_right'"
                          aria-hidden="true"
                        ></span>
                      </button>
                    </td>
                  }
                  @for (column of columns(); track column.index) {
                    <td
                      role="gridcell"
                      [style.text-align]="align(column)"
                      [attr.data-search]="column.def.allowSearch() || null"
                    >
                      @if (column.def.cell(); as cell) {
                        <ng-container
                          [ngTemplateOutlet]="cell.template"
                          [ngTemplateOutletContext]="{
                            $implicit: item.row,
                            value: item.values[column.index],
                          }"
                        />
                      } @else {
                        {{ item.texts[column.index] }}
                      }
                    </td>
                  }
                </tr>
                @if (isOpen(item)) {
                  <tr role="row" class="sck-table__detail">
                    <td
                      role="gridcell"
                      [attr.colspan]="totalColumns()"
                      [attr.aria-roledescription]="text.detailCell"
                    >
                      <ng-container
                        [ngTemplateOutlet]="detail()!.template"
                        [ngTemplateOutletContext]="{ $implicit: item.row }"
                      />
                    </td>
                  </tr>
                }
              }
            } @empty {
              <tr class="sck-table__message-row">
                <td [attr.colspan]="totalColumns()">
                  @if (failed()) {
                    <span class="sck-table__error">{{ text.loadError }}</span>
                    <button type="button" class="sck-table__retry" (click)="reload()">
                      {{ text.retry }}
                    </button>
                  } @else if (!loading()) {
                    <span class="sck-table__no-data">{{ noDataText() }}</span>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
        @if (failed() && view().length) {
          <div class="sck-table__error-bar">
            <span class="sck-table__error">{{ text.loadError }}</span>
            <button type="button" class="sck-table__retry" (click)="reload()">
              {{ text.retry }}
            </button>
          </div>
        }
      </div>
      <ng-template #menuPanel>
        <div class="sck-menu" role="menu" (keydown)="onMenuKey($event)">
          @for (entry of menuItems(); track entry.label) {
            @if (entry.separator) {
              <div class="sck-menu__separator" role="separator"></div>
            }
            <button
              type="button"
              class="sck-menu__item"
              role="menuitem"
              [attr.aria-disabled]="entry.disabled || null"
              [disabled]="entry.disabled"
              (click)="runMenu(entry)"
            >
              @if (entry.icon) {
                <span
                  class="material-icons sck-icon sck-menu__icon"
                  [attr.data-icon]="entry.icon"
                  aria-hidden="true"
                ></span>
              }
              <span class="sck-menu__text">{{ entry.label }}</span>
            </button>
          }
        </div>
      </ng-template>
      <ng-template #filterDialog>
        @if (filterPanel(); as panel) {
          <div
            class="sck-filter"
            role="dialog"
            [attr.aria-label]="text.filterOptions"
            cdkTrapFocus
            [cdkTrapFocusAutoCapture]="true"
            (keydown.escape)="closeFilter()"
          >
            <div class="sck-filter__body">
              <div
                class="sck-filter__select-all"
                role="checkbox"
                tabindex="0"
                [attr.aria-checked]="allSelected(panel.options)"
                (click)="toggleAll()"
                (keydown.space)="$event.preventDefault(); toggleAll()"
              >
                <span
                  class="sck-filter__box"
                  [class.sck-filter__box--checked]="allSelected(panel.options) === 'true'"
                  [class.sck-filter__box--mixed]="allSelected(panel.options) === 'mixed'"
                ></span>
                <span class="sck-filter__select-all-text">{{ text.selectAll }}</span>
              </div>
              <div class="sck-filter__list" role="listbox" aria-multiselectable="true">
                @for (option of panel.options; track option.key) {
                  <div
                    class="sck-filter__item"
                    role="option"
                    tabindex="0"
                    [attr.aria-selected]="option.selected"
                    (click)="toggleOption(option.key)"
                    (keydown.space)="$event.preventDefault(); toggleOption(option.key)"
                  >
                    <span
                      class="sck-filter__box"
                      [class.sck-filter__box--checked]="option.selected"
                    ></span>
                    <span class="sck-filter__item-content">{{ option.text }}</span>
                  </div>
                }
              </div>
            </div>
            <div class="sck-filter__buttons">
              <button type="button" class="sck-filter__button" (click)="applyFilter()">
                {{ text.ok }}
              </button>
              <button type="button" class="sck-filter__button" (click)="closeFilter()">
                {{ text.cancel }}
              </button>
            </div>
          </div>
        }
      </ng-template>
      @if (pageCount() > 1) {
        <div class="sck-table__pager" role="navigation" [attr.aria-label]="text.pager">
          <div class="sck-table__pages">
            @for (n of pages(); track n) {
              <button
                type="button"
                class="sck-table__page"
                [attr.aria-label]="text.page(n)"
                [attr.aria-current]="n === page() ? 'page' : null"
                (click)="goToPage(n)"
              >
                {{ n }}
              </button>
            }
          </div>
        </div>
      }
    </div>
  `,
})
export class ScTableComponent<T = unknown> {
  /** Modo local: todas las filas. */
  readonly rows = input<readonly T[]>();
  /** Modo remoto (si no hay `rows`): la fuente que pide cada página a la API. */
  readonly source = input<GridSource<T>>();
  /** Campo con la clave de la fila (para seguir filas y detalles). */
  readonly keyField = input<string>();
  /** Filas por página; 0 = sin paginar (sólo en local: la API pagina siempre). */
  readonly pageSize = input(0);
  readonly searchPanel = input(false);
  readonly groupPanel = input(false);
  readonly refreshButton = input(false);
  readonly rowAlternation = input(true);
  readonly wordWrap = input(true);
  readonly allowColumnReordering = input(false);
  readonly allowColumnResizing = input(false);
  readonly noDataText = input<string>(UI_TEXT.empty);
  /** Filtro de valores en los encabezados (dxo-header-filter). */
  readonly headerFilter = input(false);

  /** «Actualizar»: la pantalla vuelve a pedir sus filas (en remoto la tabla también recarga). */
  readonly refresh = output<void>();

  protected readonly text = UI_TEXT;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly declared = contentChildren(ScColumnDirective);
  protected readonly detail = contentChild(ScDetailDirective);

  /** Columnas visibles, en el orden en que se ven. */
  /** Id de la lista del panel de grupos (los encabezados se arrastran ahí para agrupar). */
  protected readonly groupListId = `sck-table-groups-${nextTableId++}`;
  /** Se puede agrupar: tabla local con panel de grupos. */
  protected readonly canGroup = computed(() => this.local() && this.groupPanel());

  /** Columnas agrupadas (índices declarados), por nivel; arrancan en los `groupIndex`. */
  protected readonly groupBy = linkedSignal<number[]>(() =>
    this.declared()
      .map((def, index) => ({ level: def.groupIndex(), index }))
      .filter((item): item is { level: number; index: number } => item.level !== undefined)
      .sort((a, b) => a.level - b.level)
      .map((item) => item.index),
  );
  /** La agrupación sólo existe en modo local (Ruling 1 del plan de la fase 6b). */
  protected readonly groupLevels = computed(() => (this.local() ? this.groupBy() : []));
  protected readonly groupSpaces = computed(() => this.groupLevels().map((_, level) => level));
  /** Grupos cerrados (por su camino de claves); los demás están abiertos (autoExpandAll). */
  private readonly collapsed = signal(new Set<string>());

  /** Orden de las columnas (índices declarados); vuelve al declarado si cambian las columnas. */
  private readonly order = linkedSignal(() => this.declared().map((_, index) => index));
  protected readonly columns = computed<Column[]>(() => {
    const declared = this.declared();
    return this.order()
      .filter((index) => declared[index])
      .map((index) => ({ def: declared[index], index }))
      .filter((column) => column.def.visible() && !this.groupLevels().includes(column.index));
  });
  /** Un arrastre de encabezado termina con un clic: ese clic no ordena (como en DX). */
  protected headerDragged = false;
  /** Anchos fijados por la persona (índice declarado → px). */
  protected readonly widths = signal(new Map<number, number>());

  /** Orden actual, por índice declarado; arranca en el `sortOrder` de las columnas. */
  protected readonly sort = linkedSignal<{ index: number; dir: 'asc' | 'desc' }[]>(() =>
    this.declared()
      .map((def, index) => ({ index, dir: def.sortOrder() }))
      .filter((item): item is { index: number; dir: 'asc' | 'desc' } => !!item.dir),
  );
  protected readonly searchText = signal('');
  private readonly appliedSearch = signal('');
  protected readonly page = signal(1);
  private readonly open = signal(new Set<unknown>());

  // ── Remoto ──
  protected readonly loading = signal(false);
  protected readonly failed = signal(false);
  private readonly remote = signal<{ rows: unknown[]; total: number }>({ rows: [], total: 0 });
  private readonly reloads = signal(0);
  private request?: Subscription;
  private searchTimer?: ReturnType<typeof setTimeout>;

  private readonly local = computed(() => this.rows() !== undefined);
  private readonly effectivePageSize = computed(() =>
    this.local()
      ? this.groupLevels().length
        ? 0
        : Math.max(0, this.pageSize())
      : this.pageSize() || REMOTE_PAGE_SIZE,
  );

  /** Lista permitida del modo local: `v<i>` (valor, se ordena) y `t<i>` (texto, se busca). */
  private readonly localSpec = computed<GridSpec>(() => {
    const fields: GridSpec['fields'] = {};
    this.declared().forEach((def, index) => {
      fields[`v${index}`] = {
        type: DATA_TYPE[def.dataType()],
        sort: true,
        filter: true,
        facet: true,
      };
      fields[`t${index}`] = { type: 'string', search: def.allowSearch() };
    });
    return { key: 'n', fields };
  });

  private readonly localRows = computed<LocalRow[]>(() => {
    const defs = this.declared();
    const keyField = this.keyField();
    return (this.rows() ?? []).map((row, n) => {
      const wrapped: LocalRow = { row, n, id: keyField ? getValue(row, keyField) : n };
      defs.forEach((def, index) => {
        const field = def.field();
        const compute = def.value();
        const value = compute ? compute(row) : field ? getValue(row, field) : undefined;
        wrapped[`v${index}`] = value;
        wrapped[`t${index}`] = textOf(def, value, row);
      });
      return wrapped;
    });
  });

  private readonly query = computed<GridQuery>(() => {
    const defs = this.declared();
    const local = this.local();
    const groups = this.groupLevels();
    const state = this.sort();
    // Con grupos, primero sus campos (en su dirección) y después el orden elegido.
    const sort: GridSort[] = [
      ...groups.map((index) => ({
        field: `v${index}`,
        dir: state.find((item) => item.index === index)?.dir ?? ('asc' as const),
      })),
      ...state
        .filter(({ index }) => defs[index] && !groups.includes(index))
        .map(({ index, dir }) => ({
          field: local ? `v${index}` : (defs[index].field() ?? ''),
          dir,
        })),
    ];
    return {
      page: this.page(),
      pageSize: this.effectivePageSize(),
      sort,
      search: this.appliedSearch(),
      filters: [...this.filters()]
        .filter(([index]) => defs[index])
        .map(([index, values]) => ({
          field: local ? `v${index}` : (defs[index].field() ?? ''),
          op: 'in' as const,
          values,
        })),
      groupBy: [],
    };
  });

  private readonly result = computed<{ rows: ViewRow[]; total: number }>(() => {
    if (this.local()) {
      const result = applyGridQuery(this.localRows(), this.query(), this.localSpec());
      const rows = 'rows' in result ? result.rows : [];
      const count = this.declared().length;
      return {
        total: 'total' in result ? result.total : 0,
        rows: rows.map((item) => ({
          key: item.id,
          row: item.row,
          values: Array.from({ length: count }, (_, i) => item[`v${i}`]),
          texts: Array.from({ length: count }, (_, i) => item[`t${i}`] as string),
        })),
      };
    }
    const defs = this.declared();
    const keyField = this.keyField();
    const { rows, total } = this.remote();
    return {
      total,
      rows: rows.map((row, n) => {
        const values = defs.map((def) => {
          const field = def.field();
          return field ? getValue(row, field) : def.value()?.(row);
        });
        return {
          key: keyField ? getValue(row, keyField) : n,
          row,
          values,
          texts: defs.map((def, i) => textOf(def, values[i], row)),
        };
      }),
    };
  });

  protected readonly view = computed(() => this.result().rows);
  protected readonly totalColumns = computed(
    () => this.groupLevels().length + (this.detail() ? 1 : 0) + this.columns().length,
  );

  /**
   * Las filas, con sus filas de grupo: se recorren ya ordenadas (campos de grupo primero) y se abre
   * un grupo cada vez que cambia la clave de un nivel. Un grupo cerrado esconde lo que tiene debajo.
   * El orden de la consulta no distingue mayúsculas ni tildes; los grupos sí (clave exacta), así que
   * antes se juntan las filas de cada clave sin cambiar su orden (compareKeys desempata por código).
   */
  protected readonly items = computed<ViewItem[]>(() => {
    const levels = this.groupLevels();
    if (!levels.length)
      return this.view().map((row, i) => ({
        kind: 'row' as const,
        id: row.key,
        row,
        alt: i % 2 === 1,
      }));
    const defs = this.declared();
    const types = levels.map((index) => DATA_TYPE[defs[index].dataType()]);
    const dirs = levels.map(
      (index) => this.sort().find((item) => item.index === index)?.dir ?? 'asc',
    );
    const keyAt = (row: ViewRow, level: number) =>
      groupKey(row.values[levels[level]], types[level]);
    const rows = [...this.view()].sort((a, b) => {
      for (let level = 0; level < levels.length; level++) {
        const order = compareKeys(keyAt(a, level), keyAt(b, level), types[level], dirs[level]);
        if (order) return order;
      }
      return 0;
    });
    const pathOf = (row: ViewRow) => {
      const keys = levels.map((_, level) => keyAt(row, level));
      return levels.map((_, level) => JSON.stringify(keys.slice(0, level + 1)));
    };
    const paths = rows.map(pathOf);
    const counts = new Map<string, number>();
    for (const ids of paths) for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
    const collapsed = this.collapsed();
    const items: ViewItem[] = [];
    let previous: string[] = [];
    let data = 0;
    rows.forEach((row, n) => {
      const ids = paths[n];
      ids.forEach((id, level) => {
        if (id === previous[level] || ids.slice(0, level).some((up) => collapsed.has(up))) return;
        const index = levels[level];
        items.push({
          kind: 'group',
          id,
          level,
          open: !collapsed.has(id),
          column: { def: defs[index], index },
          group: {
            key: keyAt(row, level),
            value: row.values[index],
            count: counts.get(id) ?? 0,
            text: `${defs[index].caption()}: ${row.texts[index]}`,
          },
        });
      });
      previous = ids;
      if (!ids.some((id) => collapsed.has(id)))
        items.push({ kind: 'row', id: row.key, row, alt: data++ % 2 === 1 });
    });
    return items;
  });
  protected readonly pageCount = computed(() => {
    const size = this.effectivePageSize();
    return size ? Math.ceil(this.result().total / size) : 1;
  });
  protected readonly pages = computed(() =>
    Array.from({ length: this.pageCount() }, (_, i) => i + 1),
  );
  protected readonly ariaLabel = computed(() =>
    this.text.grid(this.result().total, this.columns().length),
  );

  constructor() {
    // Remoto: cada cambio de la consulta (o «Reintentar»/«Actualizar») pide de nuevo y cancela lo anterior.
    effect(() => {
      const source = this.source();
      const query = this.query();
      this.reloads();
      if (this.local() || !source) return;
      untracked(() => this.fetch(source, query));
    });
    inject(DestroyRef).onDestroy(() => {
      this.request?.unsubscribe();
      clearTimeout(this.searchTimer);
      paintHighlights(this, []);
    });
    // Resaltado de la búsqueda sobre el texto ya pintado (sin tocar el DOM de las celdas).
    afterRenderEffect(() => {
      this.view();
      const needle = normalizeNeedle(this.appliedSearch());
      const ranges: Range[] = [];
      if (needle) {
        const cells = this.host.nativeElement.querySelectorAll(
          'tr.sck-table__row > td[data-search]',
        );
        cells.forEach((cell) => {
          const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
          for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            ranges.push(...matchRanges(node as Text, needle));
          }
        });
      }
      paintHighlights(this, ranges);
    });
  }

  // ── Filtro de encabezado ──

  /** Valores elegidos por columna (índice declarado → valores del filtro `in`). */
  protected readonly filters = signal(new Map<number, unknown[]>());
  protected readonly filterPanel = signal<{ column: Column; options: FilterOption[] } | null>(null);
  private readonly filterTemplate = viewChild.required<TemplateRef<unknown>>('filterDialog');
  private readonly overlay = inject(Overlay);
  private readonly viewContainer = inject(ViewContainerRef);
  private filterRef?: OverlayRef;
  private filterTrigger?: HTMLElement;

  /** Con campo, sin `allowHeaderFiltering = false` y no de fecha (ninguna lo usa hoy). */
  protected filterable(column: Column): boolean {
    const type = column.def.dataType();
    return (
      this.headerFilter() &&
      column.def.allowHeaderFiltering() &&
      !!column.def.field() &&
      type !== 'date' &&
      type !== 'datetime'
    );
  }

  /**
   * Abre «Opciones de filtro» bajo el encabezado, con los valores del conjunto que pasa la búsqueda y
   * los demás filtros (como Excel). «(Vacío)» junta los vacíos y el texto vacío, como DevExtreme.
   */
  protected async openFilter(column: Column, event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.closeFilter();
    const trigger = event.target as HTMLElement;
    this.filterTrigger = trigger;
    const local = this.local();
    const query: GridQuery = {
      ...this.query(),
      page: 1,
      pageSize: local ? 0 : this.effectivePageSize(),
      view: 'facet',
      facetField: local ? `v${column.index}` : (column.def.field() ?? ''),
    };
    const result = local
      ? applyGridQuery(this.localRows(), query, this.localSpec())
      : await firstValueFrom(this.source()!.load(query));
    const values = 'values' in result ? result.values : [];
    const current = this.filters().get(column.index) ?? [];
    const options: FilterOption[] = [];
    if (values.some((item) => item.value === null || item.value === ''))
      options.push({ key: 'e:', text: this.text.emptyValue, values: [null, ''], selected: false });
    for (const item of values) {
      if (item.value === null || item.value === '') continue;
      options.push({
        key: `v:${String(item.value)}`,
        text: textOf(column.def, item.value, undefined),
        values: [item.value],
        selected: false,
      });
    }
    for (const option of options)
      option.selected = option.values.some((value) => current.includes(value));
    this.filterPanel.set({ column, options });

    const anchor = trigger.closest('th, .sck-table__group-chip') ?? trigger;
    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(anchor)
      .withPositions([
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
        { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
      ])
      .withFlexibleDimensions(false)
      // Como DX (collision «fit»): junto a un borde, la ventana se corre para quedar entera.
      .withPush(true);
    this.filterRef = this.overlay.create({
      positionStrategy: strategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'sck-filter-pane',
      usePopover: false,
    });
    this.filterRef.backdropClick().subscribe(() => this.closeFilter());
    const ref = this.filterRef;
    // En píxeles enteros (el encabezado puede estar en una posición fraccionaria), como sc-select.
    strategy.positionChanges.subscribe(() => snapToPixels(ref.overlayElement));
    ref.attach(new TemplatePortal(this.filterTemplate(), this.viewContainer));
    snapToPixels(ref.overlayElement);
  }

  protected closeFilter(): void {
    if (!this.filterRef) return;
    this.filterRef.dispose();
    this.filterRef = undefined;
    this.filterPanel.set(null);
    this.filterTrigger?.focus();
  }

  protected allSelected(options: FilterOption[]): 'true' | 'false' | 'mixed' {
    const count = options.filter((option) => option.selected).length;
    return count === 0 ? 'false' : count === options.length ? 'true' : 'mixed';
  }

  protected toggleAll(): void {
    this.filterPanel.update((panel) => {
      if (!panel) return panel;
      const all = this.allSelected(panel.options) !== 'true';
      return { ...panel, options: panel.options.map((option) => ({ ...option, selected: all })) };
    });
  }

  protected toggleOption(key: string): void {
    this.filterPanel.update(
      (panel) =>
        panel && {
          ...panel,
          options: panel.options.map((option) =>
            option.key === key ? { ...option, selected: !option.selected } : option,
          ),
        },
    );
  }

  /** Aceptar: filtra por lo elegido (nada elegido = sin filtro) y vuelve a la página 1. */
  /** Aceptar: lo marcado filtra; nada o todo marcado (como DX) deja la columna sin filtro. */
  protected applyFilter(): void {
    const panel = this.filterPanel();
    if (!panel) return;
    const all = this.allSelected(panel.options) === 'true';
    const values = panel.options
      .filter((option) => option.selected)
      .flatMap((option) => option.values);
    this.filters.update((filters) => {
      const next = new Map(filters);
      if (values.length && !all) next.set(panel.column.index, values);
      else next.delete(panel.column.index);
      return next;
    });
    this.page.set(1);
    this.closeFilter();
  }

  protected sortable(column: Column): boolean {
    return column.def.allowSorting() && !!column.def.field();
  }

  protected sortDir(column: Column): 'asc' | 'desc' | null {
    return this.sort().find((item) => item.index === column.index)?.dir ?? null;
  }

  /**
   * Lugar de los íconos del encabezado que cuenta para el ancho de la columna: sólo con filtro de
   * encabezado (ver kit.css), 3px + 14px por ícono y 12px más con el número de orden.
   */
  protected reserve(column: Column): number {
    if (!this.headerFilter()) return 0;
    const icons = (this.sortable(column) ? 1 : 0) + (this.filterable(column) ? 1 : 0);
    if (!icons) return 0;
    return 3 + 14 * icons + (this.sortIndex(column) === null ? 0 : 12);
  }

  /** Su lugar en el orden (1, 2…) si hay más de una columna ordenada; los grupos no cuentan. */
  protected sortIndex(column: Column): number | null {
    const groups = this.groupLevels();
    const sorted = this.sort().filter((item) => !groups.includes(item.index));
    if (sorted.length < 2) return null;
    const at = sorted.findIndex((item) => item.index === column.index);
    return at < 0 ? null : at + 1;
  }

  protected ariaSort(column: Column): string {
    const dir = this.sortDir(column);
    return dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none';
  }

  protected align(column: Column): string {
    return column.def.alignment() ?? (column.def.dataType() === 'number' ? 'right' : 'left');
  }

  /** Clic en un encabezado: asc ↔ desc; con Shift suma (o invierte) esta columna. */
  protected onHeaderClick(column: Column, event: Event): void {
    if (!this.sortable(column) || this.headerDragged) return;
    const current = this.sort();
    const mine = current.find((item) => item.index === column.index);
    const flipped: 'asc' | 'desc' = mine?.dir === 'asc' ? 'desc' : 'asc';
    if ((event as MouseEvent).shiftKey) {
      this.sort.set(
        mine
          ? current.map((item) => (item.index === column.index ? { ...item, dir: flipped } : item))
          : [...current, { index: column.index, dir: 'asc' }],
      );
    } else {
      // El orden de los grupos se conserva (como en DX: se cambia desde su ficha).
      const groups = this.groupLevels();
      this.sort.set([
        ...current.filter((item) => groups.includes(item.index)),
        { index: column.index, dir: mine ? flipped : 'asc' },
      ]);
    }
    this.page.set(1);
  }

  // ── Agrupación (sólo local) ──

  protected groupable(column: Column): boolean {
    return this.canGroup() && column.def.allowGrouping() && !!column.def.field();
  }

  /** La columna declarada en `index` (las agrupadas no están entre las visibles). */
  protected columnAt(index: number): Column {
    return { def: this.declared()[index], index };
  }

  protected groupDir(index: number): 'asc' | 'desc' {
    return this.sort().find((item) => item.index === index)?.dir ?? 'asc';
  }

  /** Sólo entran al panel los encabezados de columnas que se pueden agrupar. */
  protected readonly canDropOnPanel = (drag: CdkDrag<number>): boolean => {
    const def = this.declared()[drag.data];
    return this.canGroup() && !!def?.allowGrouping() && !!def.field();
  };

  /** Como en el panel de DX: el nivel es el de la primera ficha cuyo centro queda a la derecha. */
  protected onGroupDrop(event: CdkDragDrop<unknown, unknown, number>): void {
    if (event.previousContainer === event.container) return;
    const panel = event.container.element.nativeElement as HTMLElement;
    const before = [...panel.querySelectorAll<HTMLElement>('.sck-table__group-chip')].filter(
      (chip) => {
        const box = chip.getBoundingClientRect();
        return box.left + box.width / 2 < event.dropPoint.x;
      },
    );
    this.groupByColumn(event.item.data, before.length);
  }

  // ── Menú contextual (encabezados y fichas del panel) ──

  protected readonly menuItems = signal<MenuEntry[]>([]);
  private readonly menuTemplate = viewChild.required<TemplateRef<unknown>>('menuPanel');
  private menuRef?: OverlayRef;

  /**
   * El de DX (getContextMenuItems): ordenar si la columna se ordena (la dirección que ya tiene,
   * deshabilitada), agrupar si se agrupa y mover si se reordena. Sin ítems, no hay menú.
   */
  protected openHeaderMenu(column: Column, event: MouseEvent): void {
    const dir = this.sortDir(column);
    const visible = this.columns();
    const position = visible.findIndex((item) => item.index === column.index);
    const entries: MenuEntry[] = [];
    if (this.sortable(column)) {
      entries.push(
        {
          label: this.text.sortAscending,
          icon: 'arrow_upward',
          disabled: dir === 'asc',
          action: () => this.sortFromMenu(column, 'asc'),
        },
        {
          label: this.text.sortDescending,
          icon: 'arrow_downward',
          disabled: dir === 'desc',
          action: () => this.sortFromMenu(column, 'desc'),
        },
        {
          label: this.text.clearSorting,
          icon: 'swap_vert',
          disabled: !dir,
          action: () => this.sortFromMenu(column, null),
        },
      );
    }
    if (this.groupable(column)) {
      entries.push(
        {
          label: this.text.groupByColumn,
          icon: 'table_rows',
          separator: entries.length > 0,
          action: () => this.groupByColumn(column.index),
        },
        { label: this.text.ungroupAll, icon: 'layers_clear', action: () => this.ungroupAll() },
      );
    }
    if (this.allowColumnReordering()) {
      entries.push(
        {
          label: this.text.moveLeft,
          icon: 'arrow_back',
          separator: entries.length > 0,
          disabled: position <= 0,
          action: () => this.moveColumn(column, -1),
        },
        {
          label: this.text.moveRight,
          icon: 'arrow_forward',
          disabled: position >= visible.length - 1,
          action: () => this.moveColumn(column, 1),
        },
      );
    }
    if (entries.length) this.openMenu(entries, event);
  }

  protected openChipMenu(index: number, event: MouseEvent): void {
    this.openMenu(
      [
        { label: this.text.ungroup, icon: 'clear', action: () => this.ungroup(index) },
        { label: this.text.ungroupAll, icon: 'layers_clear', action: () => this.ungroupAll() },
      ],
      event,
    );
  }

  protected runMenu(entry: MenuEntry): void {
    if (entry.disabled) return;
    this.closeMenu();
    entry.action();
  }

  protected closeMenu(): void {
    this.menuRef?.dispose();
    this.menuRef = undefined;
  }

  /** Flechas entre los ítems habilitados; Escape cierra. */
  protected onMenuKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMenu();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const buttons = [
      ...(event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not([disabled])',
      ),
    ];
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? at + 1 : at - 1;
    buttons[(next + buttons.length) % buttons.length]?.focus();
  }

  private openMenu(entries: MenuEntry[], event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.closeMenu();
    this.menuItems.set(entries);
    const strategy = this.overlay
      .position()
      .flexibleConnectedTo({ x: event.clientX, y: event.clientY })
      .withPositions([
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
        { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
      ])
      .withPush(true);
    this.menuRef = this.overlay.create({
      positionStrategy: strategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'sck-menu-pane',
      usePopover: false,
    });
    this.menuRef.backdropClick().subscribe(() => this.closeMenu());
    this.menuRef.keydownEvents().subscribe((key) => {
      if (key.key === 'Escape') this.closeMenu();
    });
    this.menuRef.attach(new TemplatePortal(this.menuTemplate(), this.viewContainer));
    this.menuRef.overlayElement
      .querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])')
      ?.focus();
  }

  /** Como DX con orden múltiple: sólo cambia esta columna (si no estaba ordenada, va al final). */
  private sortFromMenu(column: Column, dir: 'asc' | 'desc' | null): void {
    this.sort.update((sort) => {
      if (!dir) return sort.filter((item) => item.index !== column.index);
      return sort.some((item) => item.index === column.index)
        ? sort.map((item) => (item.index === column.index ? { ...item, dir } : item))
        : [...sort, { index: column.index, dir }];
    });
    this.page.set(1);
  }

  /** «Mover a la izquierda/derecha»: cambia de lugar con la columna visible de al lado. */
  private moveColumn(column: Column, step: -1 | 1): void {
    const visible = this.columns().map((item) => item.index);
    const from = visible.indexOf(column.index);
    const to = from + step;
    if (from < 0 || to < 0 || to >= visible.length) return;
    moveItemInArray(visible, from, to);
    let next = 0;
    const shown = new Set(visible);
    this.order.update((order) =>
      order.map((index) => (shown.has(index) ? visible[next++] : index)),
    );
  }

  /** Agrupa por la columna en el nivel `level` (por omisión, el siguiente), si se puede. */
  groupByColumn(index: number, level?: number): void {
    const def = this.declared()[index];
    if (!this.local() || !def?.allowGrouping() || this.groupBy().includes(index)) return;
    this.groupBy.update((groups) => {
      const next = [...groups];
      next.splice(level ?? next.length, 0, index);
      return next;
    });
  }

  ungroup(index: number): void {
    this.groupBy.update((groups) => groups.filter((item) => item !== index));
  }

  ungroupAll(): void {
    this.groupBy.set([]);
  }

  /** Invierte el orden de los grupos de una columna (clic en su ficha). */
  toggleGroupSort(index: number): void {
    this.sort.update((sort) => {
      const mine = sort.find((item) => item.index === index);
      const dir: 'asc' | 'desc' = mine?.dir === 'desc' ? 'asc' : 'desc';
      return mine
        ? sort.map((item) => (item.index === index ? { ...item, dir } : item))
        : [...sort, { index, dir }];
    });
  }

  protected toggleGroup(id: string): void {
    this.collapsed.update((collapsed) => {
      const next = new Set(collapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  protected onSearch(value: string | null): void {
    this.searchText.set(value ?? '');
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.appliedSearch.set(this.searchText());
      this.page.set(1);
    }, SEARCH_DELAY_MS);
  }

  protected onRefresh(): void {
    this.refresh.emit();
    if (!this.local()) this.reload();
  }

  protected reload(): void {
    this.reloads.update((n) => n + 1);
  }

  /** Encabezado soltado en otra posición: la columna pasa ahí (las ocultas quedan donde estaban). */
  protected onColumnDrop(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex === event.currentIndex) return;
    const visible = this.columns().map((column) => column.index);
    moveItemInArray(visible, event.previousIndex, event.currentIndex);
    let next = 0;
    const shown = new Set(visible);
    this.order.update((order) =>
      order.map((index) => (shown.has(index) ? visible[next++] : index)),
    );
  }

  protected endHeaderDrag(): void {
    setTimeout(() => (this.headerDragged = false));
  }

  /** Arrastre del borde derecho de un encabezado: fija el ancho de la columna (con su mínimo). */
  protected onResizeStart(column: Column, event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const header = (event.target as HTMLElement).closest('th')!;
    const startX = event.clientX;
    const startWidth = header.getBoundingClientRect().width;
    const min = column.def.minWidth() ?? MIN_COLUMN_WIDTH;
    const move = (e: PointerEvent) => {
      const width = Math.max(min, Math.round(startWidth + e.clientX - startX));
      this.widths.update((widths) => new Map(widths).set(column.index, width));
    };
    const end = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
      setTimeout(() => (this.headerDragged = false));
    };
    this.headerDragged = true;
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
  }

  protected goToPage(page: number): void {
    this.page.set(page);
  }

  protected isOpen(item: ViewRow): boolean {
    return !!this.detail() && this.open().has(item.key);
  }

  protected toggle(item: ViewRow): void {
    this.open.update((open) => {
      const next = new Set(open);
      if (next.has(item.key)) next.delete(item.key);
      else next.add(item.key);
      return next;
    });
  }

  private fetch(source: GridSource<T>, query: GridQuery): void {
    this.request?.unsubscribe();
    this.loading.set(true);
    this.failed.set(false);
    this.request = source.load(query).subscribe({
      next: (result) => {
        this.remote.set({
          rows: 'rows' in result ? (result.rows as unknown[]) : [],
          total: 'total' in result ? result.total : 0,
        });
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.failed.set(true);
      },
    });
  }
}
